import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useGameStore from '@/store/gameStore';

function GameOverPhase() {
  const navigate = useNavigate();
  const { gameState, isHost } = useGameStore();

  if (!gameState) return null;

  const totalCards = gameState.deck.successCount + gameState.deck.failedCount;
  const successRate =
    totalCards > 0 ? Math.round((gameState.deck.successCount / totalCards) * 100) : 0;

  const getScoreRating = () => {
    if (successRate >= 80) return { emoji: '🏆', text: 'Outstanding!', color: 'text-yellow-500' };
    if (successRate >= 60) return { emoji: '🎯', text: 'Great Job!', color: 'text-success-600' };
    if (successRate >= 40) return { emoji: '👍', text: 'Good Effort!', color: 'text-blue-600' };
    if (successRate >= 20) return { emoji: '💪', text: 'Keep Trying!', color: 'text-orange-600' };
    return { emoji: '😅', text: 'Better Luck Next Time!', color: 'text-gray-600' };
  };

  const rating = getScoreRating();

  const handleReturnToLobby = () => {
    navigate('/lobby');
  };

  return (
    <div className="py-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-lg mx-auto"
      >
        {/* Trophy Animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 150, damping: 20 }}
          className="text-8xl mb-6"
        >
          {rating.emoji}
        </motion.div>

        <h1 className="text-4xl font-bold text-gray-800 mb-2">Game Over!</h1>
        <p className={`text-2xl font-semibold mb-6 ${rating.color}`}>{rating.text}</p>

        {/* Final Score */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-success-50 rounded-xl"
          >
            <p className="text-4xl font-bold text-success-600 mb-1">
              {gameState.deck.successCount}
            </p>
            <p className="text-sm text-success-600 font-semibold">Correct</p>
          </motion.div>

          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="p-6 bg-danger-50 rounded-xl"
          >
            <p className="text-4xl font-bold text-danger-600 mb-1">{gameState.deck.failedCount}</p>
            <p className="text-sm text-danger-600 font-semibold">Wrong/Skipped</p>
          </motion.div>
        </div>

        {/* Success Rate */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="p-6 bg-primary-50 rounded-xl mb-6"
        >
          <p className="text-sm text-primary-600 mb-2">Success Rate</p>
          <p className="text-5xl font-bold text-primary-700">{successRate}%</p>
          <div className="mt-3 h-3 bg-primary-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${successRate}%` }}
              transition={{ delay: 0.6, duration: 1 }}
              className="h-full bg-primary-500"
            />
          </div>
        </motion.div>

        {/* Round History */}
        {gameState.roundHistory && gameState.roundHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-6 max-w-4xl mx-auto"
          >
            <h3 className="font-bold text-gray-800 mb-4 text-xl">Round History</h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {gameState.roundHistory.map((round) => (
                <div
                  key={round.number}
                  className="p-4 bg-white rounded-lg shadow-sm border-2 border-gray-100"
                >
                  {/* Round Header */}
                  <div className="flex items-center justify-between mb-3 pb-3 border-b">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-primary-600">Round {round.number}</span>
                      <span className="text-gray-600">•</span>
                      <span className="font-semibold text-gray-700">
                        Target: {round.targetWord}
                      </span>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        round.result === 'SUCCESS'
                          ? 'bg-success-100 text-success-700'
                          : round.result === 'FAILURE'
                            ? 'bg-danger-100 text-danger-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {round.result === 'SUCCESS'
                        ? '✓ Correct'
                        : round.result === 'FAILURE'
                          ? '✗ Wrong'
                          : '○ Skipped'}
                    </div>
                  </div>

                  {/* Guesser and Guess */}
                  <div className="mb-3 p-2 bg-purple-50 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-purple-700">
                        Guesser: {round.guesserName}
                      </span>
                      {round.guess && (
                        <>
                          <span className="text-purple-400">→</span>
                          <span className="text-sm text-purple-600">
                            Guess: &ldquo;{round.guess}&rdquo;
                          </span>
                        </>
                      )}
                      {!round.guess && (
                        <span className="text-sm italic text-purple-400">(no guess submitted)</span>
                      )}
                    </div>
                  </div>

                  {/* Clues */}
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Clues:</p>
                    {round.clues.map((clue, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          clue.wasRemoved
                            ? 'bg-red-50 text-red-600 line-through'
                            : 'bg-blue-50 text-blue-700'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{clue.playerName}:</span>
                          <span className="font-semibold">&ldquo;{clue.text}&rdquo;</span>
                        </div>
                        {clue.wasRemoved && (
                          <span className="text-xs px-2 py-0.5 bg-red-200 rounded">
                            {clue.status === 'REMOVED_DUPLICATE' ? 'Duplicate' : 'Invalid'}
                          </span>
                        )}
                      </div>
                    ))}
                    {round.clues.length === 0 && (
                      <p className="text-sm italic text-gray-400">No clues submitted</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Players Performance */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-700 mb-3">Players</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {Object.values(gameState.players).map((player) => (
              <div
                key={player.id}
                className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-sm"
              >
                <span className="text-lg">{player.avatar}</span>
                <span className="text-sm font-medium text-gray-700">{player.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {isHost() && (
            <button
              onClick={() => window.location.reload()}
              data-testid="btn-play-again"
              className="w-full btn-primary"
            >
              Play Again
            </button>
          )}
          <button
            onClick={handleReturnToLobby}
            data-testid="btn-return-lobby"
            className="w-full btn-secondary"
          >
            Return to Lobby
          </button>
        </div>

        {/* Fun Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-700"
        >
          <p className="font-semibold mb-2">Fun Facts:</p>
          <ul className="space-y-1">
            <li>• You played {gameState.config.totalRounds} rounds</li>
            <li>• {Object.keys(gameState.players).length} players participated</li>
            {gameState.config.aiPlayerCount > 0 && (
              <li>• {gameState.config.aiPlayerCount} AI players helped out</li>
            )}
            <li>• Difficulty: {gameState.config.difficulty}</li>
          </ul>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default GameOverPhase;
